"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { isChunkLoadError, isChunkReloadOnCooldown, safeReload } from "@/lib/chunk-error-recovery";
import { COLOR_MODE_BOOTSTRAP_SCRIPT } from "@/lib/color-mode-script";
import { IconAlertWarningOutline18 } from "nucleo-ui-outline-18";
import { useEffect } from "react";
import "./globals.css";

/**
 * App-wide error boundary. Only catches errors that bubble above nested
 * error.tsx boundaries (e.g., `workflows/error.tsx`, `databases/error.tsx`).
 *
 * `global-error.tsx` replaces the root layout on error, so it must render
 * its own <html>/<body> and re-run the color-mode script — the script in
 * `app/layout.tsx` is gone at this point.
 *
 * For ChunkLoadError specifically, reload to pick up the fresh build's
 * chunk hashes. See `components/ChunkErrorRecovery.tsx` for the rationale.
 * If the 10s cooldown blocks the reload (the new build is also 404ing),
 * fall through to the themed error UI instead of leaving a blank page.
 */
export default function GlobalError(t0) {
  const $ = _c(15);
  const {
    error,
    reset
  } = t0;
  let t1;
  if ($[0] !== error) {
    t1 = isChunkLoadError(error);
    $[0] = error;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isChunk = t1;
  let t2;
  if ($[2] !== isChunk) {
    t2 = isChunk && isChunkReloadOnCooldown();
    $[2] = isChunk;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cooldownBlocked = t2;
  let t3;
  let t4;
  if ($[4] !== error || $[5] !== isChunk) {
    t3 = () => {
      if (isChunk) {
        safeReload("global-error-boundary");
        return;
      }
      console.error("[global-error]", error);
    };
    t4 = [error, isChunk];
    $[4] = error;
    $[5] = isChunk;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const showErrorUI = !isChunk || cooldownBlocked;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <head><script dangerouslySetInnerHTML={{
        __html: COLOR_MODE_BOOTSTRAP_SCRIPT
      }} /></head>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== error || $[10] !== reset || $[11] !== showErrorUI) {
    t6 = showErrorUI && <div className="flex min-h-screen w-full items-center justify-center bg-zinc-50 p-6 dark:bg-zinc-950"><div className="flex max-w-md flex-col items-center gap-6 text-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"><IconAlertWarningOutline18 className="h-8 w-8 text-red-600 dark:text-red-400" /></div><div className="space-y-2"><h2 className="text-ds-text-primary text-xl font-semibold">Something went wrong</h2><p className="body-sm">An unexpected error occurred. Try again, or head back home.</p>{error.digest && <p className="meta-xs">Error ID: {error.digest}</p>}</div><div className="flex gap-3"><Button variant="brand" onClick={reset}>Try again</Button><Button variant="white" onClick={_temp}>Go home</Button></div></div></div>;
    $[9] = error;
    $[10] = reset;
    $[11] = showErrorUI;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <html lang="en" suppressHydrationWarning={true}>{t5}<body className="bg-theme-n-8 text-theme-primary font-sans antialiased" suppressHydrationWarning={true}>{t6}</body></html>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp() {
  window.location.href = "/";
}